import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import "../Dashboard/Dashboard.css";
import CustomerData from "./CustomerData";
import GasData from "./GasData";
import GasPriceChart from "./GasPriceChart";
import GasRevenueChart from "./GasRevenueChart";
import {
  Fuel,
  DollarSign,
  Truck,
  Users,
  ReceiptText,
  TrendingUp,
  AlertCircle,
  CalendarDays,
  RefreshCw,
  BarChart3,
  CreditCard,
  Wallet,
  Activity,
} from "lucide-react";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCount = (value) => safeNumber(value).toLocaleString("en-IN");

const formatCurrency = (value) =>
  `₹${safeNumber(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const pickNumber = (item, keys = []) => {
  if (!item || typeof item !== "object") return 0;

  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
      return safeNumber(item[key]);
    }
  }

  return 0;
};

const sumByPossibleKeys = (items, keys) =>
  safeArray(items).reduce((sum, item) => sum + pickNumber(item, keys), 0);

const getFirstNumberFromObject = (data, keys = []) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return 0;

  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
      return safeNumber(data[key]);
    }
  }

  return 0;
};

const getPaymentStats = (stats) => {
  if (Array.isArray(stats)) {
    return stats.reduce(
      (acc, item) => {
        const label = String(item.status || item.payment_status || item.type || item.name || "").toLowerCase();
        const amount = pickNumber(item, ["amount", "total", "total_amount", "value", "house_charge", "charge"]);
        const count = pickNumber(item, ["count", "total_count", "invoice_count", "qty"]);

        if (label.includes("paid")) {
          acc.paidAmount += amount;
          acc.paidCount += count;
        }

        if (label.includes("unpaid") || label.includes("pending")) {
          acc.unpaidAmount += amount;
          acc.unpaidCount += count;
        }

        return acc;
      },
      { paidAmount: 0, unpaidAmount: 0, paidCount: 0, unpaidCount: 0 }
    );
  }

  if (stats && typeof stats === "object") {
    return {
      paidAmount: getFirstNumberFromObject(stats, ["paid_amount", "paid_total", "paid", "total_paid"]),
      unpaidAmount: getFirstNumberFromObject(stats, ["unpaid_amount", "unpaid_total", "unpaid", "pending_amount"]),
      paidCount: getFirstNumberFromObject(stats, ["paid_count", "total_paid_count"]),
      unpaidCount: getFirstNumberFromObject(stats, ["unpaid_count", "pending_count", "total_unpaid_count"]),
    };
  }

  return { paidAmount: 0, unpaidAmount: 0, paidCount: 0, unpaidCount: 0 };
};

const KpiCard = ({ icon: Icon, label, value, helper, tone = "blue" }) => (
  <div className={`gasdb-kpi-card gasdb-tone-${tone}`}>
    <div className="gasdb-kpi-icon">
      <Icon size={21} />
    </div>
    <div className="gasdb-kpi-content">
      <div className="gasdb-kpi-label">{label}</div>
      <div className="gasdb-kpi-value">{value}</div>
      {helper && <div className="gasdb-kpi-helper">{helper}</div>}
    </div>
  </div>
);

const InsightCard = ({ icon: Icon, title, value, note, tone = "blue", progress }) => (
  <div className={`gasdb-insight-card gasdb-tone-${tone}`}>
    <div className="gasdb-insight-top">
      <div className="gasdb-insight-icon">
        <Icon size={19} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{note}</p>
      </div>
    </div>
    <div className="gasdb-insight-value">{value}</div>
    {typeof progress === "number" && (
      <div className="gasdb-progress">
        <span style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }} />
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="gasdb-section-header">
    <div className="gasdb-section-icon">
      <Icon size={18} />
    </div>
    <div>
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const SnapshotRow = ({ label, value, pill, tone = "blue" }) => (
  <div className="gasdb-snapshot-row">
    <span>{label}</span>
    <strong>{value}</strong>
    {pill && <span className={`gasdb-pill gasdb-pill-${tone}`}>{pill}</span>}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="gasdb-empty-state">
    <Fuel size={30} />
    <h2>No Gas Dashboard Data</h2>
    <p>{message}</p>
  </div>
);

export default function Dashboard() {
  const { Post } = useApi();

  const [total_amount_of_all_invoices, setTotalAmountAllInvoice] = useState([]);
  const [freight_totals, setFreightTotals] = useState([]);
  const [top_10_invoices_based_on_total_amount, setTop10InvoicesBasedOnTotalAmount] = useState([]);
  const [latest_companies, setLatestCompanies] = useState([]);
  const [paid_unpaid_stats_for_house_charges, setPaidUnpaidStatsForHouseCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchGasData();
  }, []);

  const fetchGasData = async () => {
    try {
      setLoading(true);
      setError("");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const response = await Post("gasDashboardReport", {
        start_date: startDate,
        end_date: endDate,
      });

      const {
        total_amount_of_all_invoices = [],
        freight_totals = [],
        top_10_invoices_based_on_total_amount = [],
        latest_companies = [],
        paid_unpaid_stats_for_house_charges = [],
      } = response?.data || {};

      setTotalAmountAllInvoice(total_amount_of_all_invoices);
      setFreightTotals(freight_totals);
      setTop10InvoicesBasedOnTotalAmount(top_10_invoices_based_on_total_amount);
      setLatestCompanies(latest_companies);
      setPaidUnpaidStatsForHouseCharges(paid_unpaid_stats_for_house_charges);
    } catch (fetchError) {
      console.error("Error fetching gas dashboard data:", fetchError);
      setError(fetchError?.message || "Failed to fetch gas dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  const invoiceRevenue = useMemo(
    () =>
      sumByPossibleKeys(total_amount_of_all_invoices, [
        "total_amount",
        "amount",
        "total",
        "invoice_total",
        "revenue",
        "total_invoice_amount",
      ]),
    [total_amount_of_all_invoices]
  );

  const freightAmount = useMemo(
    () =>
      Array.isArray(freight_totals)
        ? sumByPossibleKeys(freight_totals, [
            "freight_total",
            "total_freight",
            "freight_amount",
            "freight",
            "amount",
            "total",
          ])
        : getFirstNumberFromObject(freight_totals, [
            "freight_total",
            "total_freight",
            "freight_amount",
            "freight",
            "amount",
            "total",
          ]),
    [freight_totals]
  );

  const topInvoiceAmount = useMemo(
    () =>
      pickNumber(top_10_invoices_based_on_total_amount[0], [
        "total_amount",
        "amount",
        "total",
        "invoice_total",
        "payable_amount",
      ]),
    [top_10_invoices_based_on_total_amount]
  );

  const paymentStats = useMemo(
    () => getPaymentStats(paid_unpaid_stats_for_house_charges),
    [paid_unpaid_stats_for_house_charges]
  );

  const totalHouseCharges = paymentStats.paidAmount + paymentStats.unpaidAmount;
  const paidPercentage = totalHouseCharges > 0 ? Math.round((paymentStats.paidAmount / totalHouseCharges) * 100) : 0;

  const latestCompanyCount = safeArray(latest_companies).length;
  const topInvoiceCount = safeArray(top_10_invoices_based_on_total_amount).length;

  const hasData =
    safeArray(total_amount_of_all_invoices).length > 0 ||
    safeArray(freight_totals).length > 0 ||
    safeArray(top_10_invoices_based_on_total_amount).length > 0 ||
    safeArray(latest_companies).length > 0 ||
    safeArray(paid_unpaid_stats_for_house_charges).length > 0 ||
    (paid_unpaid_stats_for_house_charges &&
      typeof paid_unpaid_stats_for_house_charges === "object" &&
      Object.keys(paid_unpaid_stats_for_house_charges).length > 0);

  const currentDateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className="responsive-box gasdb-page">
        <style>{dashboardStyles}</style>
        <div className="gasdb-loading">
          <div className="gasdb-spinner" />
          <div>
            <h3>Loading Gas Dashboard</h3>
            <p>Preparing revenue, freight, customer and invoice insights.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="responsive-box gasdb-page">
        <style>{dashboardStyles}</style>
        <div className="gasdb-empty-page gasdb-error-page">
          <AlertCircle size={38} />
          <h2>Unable to Load Dashboard</h2>
          <p>{error}</p>
          <button type="button" onClick={fetchGasData} className="gasdb-retry-btn">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="responsive-box gasdb-page">
        <style>{dashboardStyles}</style>
        <div className="gasdb-empty-page">
          <Fuel size={38} />
          <h2>No Gas Report Data Found</h2>
          <p>Create gas invoices or customer records to start viewing dashboard insights.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box gasdb-page">
      <style>{dashboardStyles}</style>

      <div className="gasdb-shell">
        <section className="gasdb-hero">
          <div>
            <div className="gasdb-kicker">
              <span className="gasdb-kicker-dot" />
              Gas Business Report
            </div>
            <h1>Gas Report Dashboard</h1>
            <p>
              Simple executive view for monthly gas revenue, freight totals, customer payment status and top invoices.
            </p>
            <div className="gasdb-date-pill">
              <CalendarDays size={14} />
              {currentDateLabel}
            </div>
          </div>

          <div className="gasdb-hero-kpis">
            <KpiCard
              icon={DollarSign}
              label="Invoice Revenue"
              value={formatCurrency(invoiceRevenue)}
              helper={`Total invoice amount for ${currentYear}`}
              tone="green"
            />
            <KpiCard
              icon={Truck}
              label="Freight Total"
              value={formatCurrency(freightAmount)}
              helper="Freight amount summary"
              tone="blue"
            />
            <KpiCard
              icon={ReceiptText}
              label="Highest Invoice"
              value={formatCurrency(topInvoiceAmount)}
              helper="Top invoice by total amount"
              tone="amber"
            />
          </div>
        </section>

        <section className="gasdb-insight-grid">
          <InsightCard
            icon={Users}
            title="Latest Companies"
            value={formatCount(latestCompanyCount)}
            note="Recent customers / companies"
            tone="purple"
          />
          <InsightCard
            icon={CreditCard}
            title="Paid House Charges"
            value={formatCurrency(paymentStats.paidAmount)}
            note={`${paidPercentage}% of tracked house charges`}
            progress={paidPercentage}
            tone="green"
          />
          <InsightCard
            icon={Wallet}
            title="Unpaid House Charges"
            value={formatCurrency(paymentStats.unpaidAmount)}
            note="Pending customer house charges"
            progress={100 - paidPercentage}
            tone={paymentStats.unpaidAmount > 0 ? "red" : "green"}
          />
          <InsightCard
            icon={TrendingUp}
            title="Top Invoices"
            value={formatCount(topInvoiceCount)}
            note="Highest value invoices available"
            tone="blue"
          />
        </section>

        <section className="gasdb-focus-grid">
          <div className="gasdb-panel">
            <SectionHeader
              icon={Activity}
              title="Business Snapshot"
              subtitle="Key gas report numbers without operational clutter"
            />

            <div className="gasdb-snapshot-list">
              <SnapshotRow
                label="Total Invoice Revenue"
                value={formatCurrency(invoiceRevenue)}
                pill={`${currentYear}`}
                tone="green"
              />
              <SnapshotRow
                label="Total Freight"
                value={formatCurrency(freightAmount)}
                pill="Freight"
                tone="blue"
              />
              <SnapshotRow
                label="Paid House Charges"
                value={formatCurrency(paymentStats.paidAmount)}
                pill={`${formatCount(paymentStats.paidCount)} Paid`}
                tone="green"
              />
              <SnapshotRow
                label="Unpaid House Charges"
                value={formatCurrency(paymentStats.unpaidAmount)}
                pill={`${formatCount(paymentStats.unpaidCount)} Pending`}
                tone={paymentStats.unpaidAmount > 0 ? "red" : "green"}
              />
            </div>
          </div>

          <div className="gasdb-panel">
            <SectionHeader
              icon={BarChart3}
              title="Monthly Gas Revenue"
              subtitle="Revenue movement for the selected year"
            />

            <div className="gasdb-component-frame gasdb-chart-frame">
              <GasRevenueChart total_amount_of_all_invoices={total_amount_of_all_invoices} />
            </div>
          </div>
        </section>

        <section className="gasdb-grid">
          <div className="gasdb-panel">
            <SectionHeader
              icon={Truck}
              title="Total Revenue Summary"
              subtitle="Freight and revenue summary"
            />

            <div className="gasdb-component-frame">
              <GasData freight_totals={freight_totals} />
            </div>
          </div>

          <div className="gasdb-panel">
            <SectionHeader
              icon={Users}
              title="Customer & Payment Status"
              subtitle="Latest companies and paid/unpaid house charges"
            />

            <div className="gasdb-component-frame">
              <CustomerData
                latest_companies={latest_companies}
                paid_unpaid_stats_for_house_charges={paid_unpaid_stats_for_house_charges}
              />
            </div>
          </div>
        </section>

        <section className="gasdb-panel">
          <SectionHeader
            icon={ReceiptText}
            title="Top 10 Invoices Based on Total Amount"
            subtitle="Highest invoice values for management review"
          />

          <div className="gasdb-component-frame gasdb-chart-frame">
            <GasPriceChart top_10_invoices_based_on_total_amount={top_10_invoices_based_on_total_amount} />
          </div>
        </section>
      </div>
    </div>
  );
}

const dashboardStyles = `
  .gasdb-page {
    min-height: 100vh;
    padding: 10px 12px 28px;
    background:
      radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%),
      radial-gradient(circle at top right, rgba(16, 185, 129, 0.07), transparent 28%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    color: #0f172a;
  }

  .gasdb-shell {
    max-width: 1450px;
    margin: 0 auto;
  }

  .gasdb-hero {
    position: relative;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(440px, 0.85fr);
    gap: 18px;
    align-items: center;
    padding: 22px;
    margin-bottom: 14px;
    border-radius: 28px;
    color: #ffffff;
    background: linear-gradient(135deg, #020617 0%, #111827 56%, #064e3b 100%);
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
  }

  .gasdb-hero::after {
    content: "";
    position: absolute;
    width: 320px;
    height: 320px;
    right: -120px;
    top: -160px;
    border-radius: 999px;
    background: rgba(52, 211, 153, 0.16);
  }

  .gasdb-hero > * {
    position: relative;
    z-index: 1;
  }

  .gasdb-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 11px;
    border-radius: 999px;
    margin-bottom: 10px;
    font-size: 11px;
    font-weight: 950;
    color: #d1fae5;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.22);
  }

  .gasdb-kicker-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
  }

  .gasdb-hero h1 {
    margin: 0;
    font-size: 31px;
    line-height: 1.08;
    font-weight: 950;
    letter-spacing: -0.045em;
  }

  .gasdb-hero p {
    max-width: 700px;
    margin: 8px 0 0;
    color: rgba(255, 255, 255, 0.80);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.6;
  }

  .gasdb-date-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 13px;
    padding: 7px 11px;
    border-radius: 999px;
    color: #bbf7d0;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.18);
    font-size: 11px;
    font-weight: 900;
  }

  .gasdb-hero-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .gasdb-kpi-card,
  .gasdb-insight-card,
  .gasdb-panel {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid #dbe3ec;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
  }

  .gasdb-kpi-card {
    display: flex;
    align-items: center;
    gap: 13px;
    min-height: 82px;
    padding: 13px;
    border-radius: 20px;
  }

  .gasdb-kpi-icon,
  .gasdb-insight-icon,
  .gasdb-section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gasdb-accent);
    background: var(--gasdb-soft);
    border: 1px solid var(--gasdb-border);
    flex-shrink: 0;
  }

  .gasdb-kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 15px;
  }

  .gasdb-kpi-label {
    color: #475569;
    font-size: 11px;
    font-weight: 950;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .gasdb-kpi-value {
    margin-top: 2px;
    color: #0f172a;
    font-size: 21px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .gasdb-kpi-helper {
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
  }

  .gasdb-insight-grid,
  .gasdb-focus-grid,
  .gasdb-grid {
    display: grid;
    gap: 14px;
    margin-bottom: 14px;
  }

  .gasdb-insight-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .gasdb-focus-grid,
  .gasdb-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gasdb-insight-card {
    border-radius: 22px;
    padding: 15px;
  }

  .gasdb-insight-top {
    display: flex;
    gap: 11px;
    align-items: flex-start;
  }

  .gasdb-insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 15px;
  }

  .gasdb-insight-card h3 {
    margin: 0;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
  }

  .gasdb-insight-card p {
    margin: 4px 0 0;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.45;
  }

  .gasdb-insight-value {
    margin-top: 14px;
    color: #0f172a;
    font-size: 24px;
    font-weight: 950;
    letter-spacing: -0.04em;
  }

  .gasdb-progress {
    width: 100%;
    height: 8px;
    margin-top: 10px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .gasdb-progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--gasdb-accent);
  }

  .gasdb-panel {
    border-radius: 24px;
    padding: 16px;
    margin-bottom: 14px;
    overflow: hidden;
  }

  .gasdb-section-header {
    display: flex;
    align-items: center;
    gap: 11px;
    margin-bottom: 14px;
  }

  .gasdb-section-icon {
    width: 40px;
    height: 40px;
    border-radius: 15px;
    --gasdb-accent: #0f172a;
    --gasdb-soft: #f8fafc;
    --gasdb-border: #dbe3ec;
  }

  .gasdb-section-header h2 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 950;
    letter-spacing: -0.025em;
  }

  .gasdb-section-header p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .gasdb-snapshot-list {
    display: grid;
    gap: 10px;
  }

  .gasdb-snapshot-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
  }

  .gasdb-snapshot-row span:first-child {
    color: #475569;
    font-size: 12px;
    font-weight: 850;
  }

  .gasdb-snapshot-row strong {
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .gasdb-component-frame {
    width: 100%;
    overflow-x: auto;
  }

  .gasdb-chart-frame {
    min-height: 280px;
  }

  .gasdb-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    padding: 5px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .gasdb-pill-blue {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .gasdb-pill-green {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .gasdb-pill-red {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .gasdb-empty-page,
  .gasdb-empty-state {
    min-height: 430px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    color: #0f172a;
  }

  .gasdb-empty-page svg,
  .gasdb-empty-state svg {
    color: #059669;
    margin-bottom: 14px;
  }

  .gasdb-error-page svg {
    color: #dc2626;
  }

  .gasdb-empty-page h2,
  .gasdb-empty-state h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 950;
  }

  .gasdb-empty-page p,
  .gasdb-empty-state p {
    max-width: 480px;
    margin: 8px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .gasdb-retry-btn {
    margin-top: 14px;
    border: none;
    border-radius: 999px;
    height: 40px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #064e3b;
    color: #ffffff;
    font-size: 13px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 24px rgba(6, 78, 59, 0.20);
  }

  .gasdb-loading {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    color: #0f172a;
  }

  .gasdb-loading h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 950;
  }

  .gasdb-loading p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .gasdb-spinner {
    width: 54px;
    height: 54px;
    border-radius: 999px;
    border: 5px solid #dcfce7;
    border-top-color: #059669;
    animation: gasdb-spin 0.8s linear infinite;
  }

  @keyframes gasdb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .gasdb-tone-blue {
    --gasdb-accent: #2563eb;
    --gasdb-soft: #eff6ff;
    --gasdb-border: #bfdbfe;
  }

  .gasdb-tone-green {
    --gasdb-accent: #059669;
    --gasdb-soft: #ecfdf5;
    --gasdb-border: #a7f3d0;
  }

  .gasdb-tone-purple {
    --gasdb-accent: #7c3aed;
    --gasdb-soft: #f3e8ff;
    --gasdb-border: #ddd6fe;
  }

  .gasdb-tone-amber {
    --gasdb-accent: #d97706;
    --gasdb-soft: #fffbeb;
    --gasdb-border: #fde68a;
  }

  .gasdb-tone-red {
    --gasdb-accent: #dc2626;
    --gasdb-soft: #fef2f2;
    --gasdb-border: #fecaca;
  }

  @media (max-width: 1200px) {
    .gasdb-hero {
      grid-template-columns: 1fr;
    }

    .gasdb-insight-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .gasdb-focus-grid,
    .gasdb-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .gasdb-page {
      padding: 8px 8px 22px;
    }

    .gasdb-hero {
      border-radius: 22px;
      padding: 18px;
    }

    .gasdb-hero h1 {
      font-size: 25px;
    }

    .gasdb-insight-grid {
      grid-template-columns: 1fr;
    }

    .gasdb-panel,
    .gasdb-insight-card {
      border-radius: 20px;
      padding: 14px;
    }

    .gasdb-snapshot-row {
      grid-template-columns: 1fr;
      align-items: flex-start;
    }
  }
`;
